import React from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";

const EFFECTIVE_DATE = "July 6, 2026";
const CONTACT_EMAIL = "privacy@therealdealfit.com";

const Section = ({ title, children }) => (
  <section className="mb-6">
    <h2 className="font-display text-lg font-bold mb-2">{title}</h2>
    <div className="text-sm text-muted-foreground space-y-2 leading-relaxed">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-10 bg-background/80 backdrop-blur border-b border-border">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="privacy-back" onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground transition-colors">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <Logo className="h-7 w-7 rounded-lg" />
          <span className="font-display font-bold">Privacy Policy</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6" data-testid="privacy-content">
        <p className="text-xs text-muted-foreground mb-6">Effective date: {EFFECTIVE_DATE}</p>

        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
          Real Deal Fitness ("we", "us", the "App") respects your privacy. This policy explains what
          information we collect, how we use it, who we share it with, and the choices you have. By
          using the App you agree to this policy.
        </p>

        <Section title="1. Information We Collect">
          <p><strong className="text-foreground">Account information:</strong> name, email, and password (stored securely, hashed).</p>
          <p><strong className="text-foreground">Profile &amp; fitness data:</strong> age, sex, height, weight, body fat %, goals, activity level, workouts, sets/weights, runs (including GPS routes), and progress entries you create.</p>
          <p><strong className="text-foreground">Wearable &amp; health data:</strong> if you connect WHOOP or Strava, we access data you authorize — e.g. recovery, sleep, strain, heart rate/HRV, cycles, and workouts/activities. You control this and can disconnect at any time.</p>
          <p><strong className="text-foreground">Photos:</strong> meal or progress photos you upload (meal photos may be analyzed to estimate nutrition).</p>
          <p><strong className="text-foreground">Payment data:</strong> handled by Stripe; we do not store your full card details.</p>
          <p><strong className="text-foreground">Usage &amp; device data:</strong> basic technical logs needed to operate and secure the service.</p>
        </Section>

        <Section title="2. How We Use Your Information">
          <p>To provide core features (workouts, running/GPS tracking, AI coaching, nutrition and macro calculations, progress tracking, community, and coaching messaging); to display wearable metrics you connect; to process subscriptions; to send account and password-reset emails; and to maintain security and improve the App.</p>
        </Section>

        <Section title="3. Third-Party Services">
          <p>We share the minimum data necessary with providers that power the App:</p>
          <p><strong className="text-foreground">WHOOP</strong> and <strong className="text-foreground">Strava</strong> — wearable/activity data you authorize via OAuth.</p>
          <p><strong className="text-foreground">Stripe</strong> — subscription payments.</p>
          <p><strong className="text-foreground">OpenAI</strong> — powers AI coaching and meal-photo nutrition estimates (relevant inputs are sent to generate a response).</p>
          <p><strong className="text-foreground">Resend</strong> — transactional emails (e.g. password reset).</p>
          <p>We do not sell your personal data.</p>
        </Section>

        <Section title="4. Wearable Data (WHOOP &amp; Strava)">
          <p>Access to WHOOP/Strava data is granted by you through their secure authorization screens and is limited to the scopes you approve. We store access tokens to sync your data and refresh them as needed. You can revoke access anytime by disconnecting inside the App, or from your WHOOP/Strava account settings. Disconnecting removes the stored tokens.</p>
        </Section>

        <Section title="5. Data Retention &amp; Deletion">
          <p>We keep your data while your account is active. You may request deletion of your account and associated data at any time. Disconnecting a wearable deletes its stored tokens; you can also delete individual runs and other entries within the App.</p>
        </Section>

        <Section title="6. Your Rights">
          <p>Depending on your location, you may have rights to access, correct, export, or delete your personal data, and to withdraw consent. To exercise these rights, contact us using the details below or use the in-app Support page.</p>
        </Section>

        <Section title="7. Security">
          <p>We use industry-standard measures (encryption in transit, hashed passwords, scoped access tokens) to protect your data. No method of transmission or storage is 100% secure, but we work to protect your information.</p>
        </Section>

        <Section title="8. Children">
          <p>The App is not intended for children under 16, and we do not knowingly collect data from them.</p>
        </Section>

        <Section title="9. Changes to This Policy">
          <p>We may update this policy from time to time. Material changes will be reflected by updating the effective date above.</p>
        </Section>

        <Section title="10. Contact Us">
          <p>Questions about this policy or your data? Email <a href={`mailto:${CONTACT_EMAIL}`} className="rd-lime font-semibold">{CONTACT_EMAIL}</a> or reach us through the in-app Support page.</p>
        </Section>

        <Button data-testid="privacy-done" onClick={() => navigate(-1)} className="w-full h-11 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold mt-2">Done</Button>
        <div className="h-8" />
      </main>
    </div>
  );
}
